// Catálogos del módulo de prospectos (asesoras).
// Los valores deben coincidir con los enums de klazia-api
// (src/api/prospect/content-types/prospect/schema.json).

export const PROSPECT_STATUS = [
  { value: 'nuevo', label: 'Nuevo', color: 'purple' },
  { value: 'intentando_contactar', label: 'Intentando contactar', color: 'orange' },
  { value: 'en_curso_tibio', label: 'En curso · Tibio', color: 'yellow' },
  { value: 'en_curso_caliente', label: 'En curso · Caliente', color: 'red' },
  { value: 'transferido', label: 'Transferido', color: 'cyan' },
  { value: 'registrado', label: 'Registrado', color: 'blue' },
  { value: 'estudiando', label: 'Estudiando', color: 'green' },
  { value: 'no_interesado', label: 'No interesado', color: 'gray' },
  { value: 'fallido', label: 'Fallido', color: 'gray' },
  { value: 'basura', label: 'Basura', color: 'gray' },
];

// Estados que la asesora sigue trabajando (la vista por defecto del Escritorio).
export const ACTIVE_STATUSES = ['nuevo', 'intentando_contactar', 'en_curso_tibio', 'en_curso_caliente', 'transferido'];
export const CLOSED_STATUSES = ['registrado', 'estudiando', 'no_interesado', 'fallido', 'basura'];

export const DESK_VIEWS = [
  { value: 'seguimiento', label: 'En seguimiento', statuses: ACTIVE_STATUSES },
  { value: 'cerrados', label: 'Cerrados', statuses: CLOSED_STATUSES },
  { value: 'todos', label: 'Todos', statuses: null },
];

export const ENTRY_CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'llamada', label: 'Llamada' },
  { value: 'landing', label: 'Formulario web' },
  { value: 'referido', label: 'Referido' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'otro', label: 'Otro' },
];

export const HOW_FOUND = [
  { value: 'pendiente', label: 'Pendiente de preguntar' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'conocido', label: 'Conocido' },
  { value: 'referido', label: 'Referido' },
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'otro', label: 'Otro' },
];

export const CONTACT_ROLES = [
  { value: 'estudiante', label: 'Es el estudiante' },
  { value: 'acudiente', label: 'Es acudiente o familiar' },
];

// El programa de interés del registro manual es el slug de un program-type
// (ver useProgramTypes). Los prospectos de la landing traen el slug de su
// página (p. ej. "bachillerato-virtual-adultos-fb"), que se conserva tal cual
// porque la fase de marketing lo usa.
export const PROGRAM_PENDING = 'por-definir';

const findLabel = (list, value) => list.find((el) => el.value === value)?.label;

export const statusInfo = (value) =>
  PROSPECT_STATUS.find((el) => el.value === value) || { value, label: value || 'Sin estado', color: 'gray' };

export const howFoundLabel = (value, detail) => {
  if (value === 'otro' && detail) return `Otro: ${detail}`;
  return findLabel(HOW_FOUND, value) || 'Sin dato';
};

export const contactRoleLabel = (value) => findLabel(CONTACT_ROLES, value);

// Los prospectos anteriores a este cambio no tienen canal: todos venían del formulario web.
export const channelLabel = (prospect) => {
  if (prospect?.entry_channel) return findLabel(ENTRY_CHANNELS, prospect.entry_channel) || prospect.entry_channel;
  return prospect?.created_manually ? 'Sin dato' : 'Formulario web';
};

// Nombre legible del programa: el del program-type si coincide el slug; si no,
// el slug sin sufijo de canal (-fb, -gl…) y con espacios.
export const programLabel = (slug, programTypes = []) => {
  if (!slug || slug === PROGRAM_PENDING) return 'Por definir';
  const base = slug.replace(/-(fb|gl|tk|display)$/, '');
  const match = programTypes.find((el) => el.slug === slug || el.slug === base);
  if (match) return match.name;
  const text = base.replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// El dispositivo no cuenta: el formulario web lo detecta aunque no haya campaña.
export const hasCampaignData = (prospect) =>
  Boolean(
    prospect?.utm_source ||
    prospect?.utm_medium ||
    prospect?.utm_campaign ||
    prospect?.utm_content ||
    prospect?.utm_term ||
    prospect?.gclid
  );

// Mismos códigos que {device} de Google Ads; el formulario web usa los mismos.
const DEVICE_LABELS = {
  m: 'Celular',
  mobile: 'Celular',
  t: 'Tablet',
  tablet: 'Tablet',
  c: 'Computador',
  desktop: 'Computador',
};
export const deviceLabel = (value) => (value ? DEVICE_LABELS[String(value).toLowerCase()] || value : '');

// Copy de ayuda del campo Observación: lo que motivó al contacto a querer el programa.
export const OBSERVATION_HELP =
  'Escribe qué motivó al contacto a buscar el programa: para qué lo necesita, por qué ahora y qué lo hizo decidirse.';
export const OBSERVATION_PLACEHOLDER =
  'Ej.: Necesita el título de bachiller para ascender en su trabajo. Trabaja de día y busca estudiar sin horarios.';

// Fuente que registra la asesora a mano cuando el contacto no trae datos de
// campaña (p. ej. escribe por WhatsApp desde un anuncio). Se guarda en
// utm_source/utm_content con utm_manual = true (klazia-api utils/manual-campaign).
export const AD_SOURCES = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'google', label: 'Google' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'otro', label: 'Otro' },
];
export const adSourceLabel = (value) => AD_SOURCES.find((el) => el.value === value)?.label || value || '';
// Datos de campaña que llegaron solos del anuncio: no se editan.
export const hasAutomaticCampaign = (prospect) => hasCampaignData(prospect) && !prospect?.utm_manual;

// Primera gestión: tiempo corrido desde el registro hasta el primer cambio de
// estado que hace la asesora (lo marca klazia-api en first_status_change_at).
export const formatDuration = (minutes) => {
  if (minutes == null) return '';
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const hours = Math.floor(m / 60);
  if (hours < 24) return m % 60 ? `${hours} h ${m % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
};
const minutesBetween = (from, to) => (new Date(to) - new Date(from)) / 60000;
// { state: 'done' | 'pending' | 'unknown', minutes }
export const firstResponseInfo = (prospect, now = new Date()) => {
  if (prospect?.first_status_change_at) {
    return { state: 'done', minutes: minutesBetween(prospect.createdAt || prospect.created_at, prospect.first_status_change_at) };
  }
  if (['nuevo', 'transferido'].includes(prospect?.status)) {
    return { state: 'pending', minutes: minutesBetween(prospect.createdAt || prospect.created_at, now) };
  }
  // Cambió de estado antes de que se empezara a medir.
  return { state: 'unknown', minutes: null };
};
